import { isValidElement } from 'react';
import { toast } from 'react-toastify';

/**
 * Single entry point for user feedback (toast) messages.
 *
 * Conventions
 *  - success : "<Subject> <past participle> successfully."       e.g. "Data saved successfully."
 *  - info    : neutral FYI, nothing the user must fix.             e.g. "There is no data to export."
 *  - warning : the user must correct something before continuing.  e.g. "Please fill in all mandatory fields."
 *  - error   : something failed; says what failed, why, and how to recover.
 *              e.g. "Failed to save data. The server could not be reached. Check your internet connection and try again."
 *
 * Messages are English, sentence case and always end with punctuation.
 * Use `notify.apiError(error, 'save data')` for any failed API call instead of
 * concatenating `error.message` by hand — the api_helper interceptor rejects
 * plain strings, so `error.message` is usually undefined.
 *
 * The one <ToastContainer /> lives in App.js. Do not add page-level containers.
 */

const DURATION = {
  success: 3000,
  info: 4000,
  warning: 5000,
  error: 7000,
};

/** Frequently used messages, so the wording stays identical everywhere. */
export const MESSAGES = {
  saved: 'Data saved successfully.',
  updated: 'Data updated successfully.',
  deleted: 'Data deleted successfully.',
  uploaded: 'File uploaded successfully.',
  mandatory: 'Please fill in all mandatory fields.',
  authRequired: 'Your session is no longer valid. Please log in again.',
  noDataToExport: 'There is no data to export.',
  noChanges: 'There are no changes to save.',
};

const withPeriod = text => (/[.!?]$/.test(text) ? text : `${text}.`);

/** Trim, collapse whitespace, drop stray trailing dots and make sure it ends with punctuation. */
export const formatMessage = message => {
  if (message === null || message === undefined) return '';
  const text = String(message)
    .replace(/\s+/g, ' ')
    .replace(/\s+([.!?,])/g, '$1')
    .replace(/\.{2,}$/, '.')
    .trim();
  return text ? withPeriod(text) : '';
};

const HINT = {
  network: 'Check your internet connection and try again.',
  retry: 'Please try again.',
  session: 'Please log in again.',
  permission: 'Contact your administrator if you need access.',
  notFound:
    'It may have been moved or deleted. Refresh the page and try again.',
  conflict: 'Someone else may have changed it. Refresh the page and try again.',
  fileSize: 'Choose a smaller file and try again.',
  validation: 'Check the data you entered and try again.',
  server: 'Try again in a moment. If the problem continues, contact support.',
};

// Generic transport-level texts (axios / fetch / api_helper interceptor). Anything else
// is a message written by the backend for the user and is kept as it is.
const RULES = {
  network: {
    test: /^(network error|failed to fetch|load failed|no response from server)$/i,
    reason: 'The server could not be reached.',
    hint: HINT.network,
  },
  timeout: {
    test: /timeout of \d+ms exceeded|timed out/i,
    reason: 'The request took too long.',
    hint: HINT.retry,
  },
  session: {
    test: /^session ended$|^unauthorized|status code 401|token tidak valid/i,
    reason: 'Your session has expired or is no longer valid.',
    hint: HINT.session,
  },
  permission: {
    test: /^forbidden$|status code 403/i,
    reason: 'You do not have permission to do this.',
    hint: HINT.permission,
  },
  notFound: {
    test: /^data tidak ditemukan$|status code 404/i,
    reason: 'The data could not be found.',
    hint: HINT.notFound,
  },
  conflict: {
    test: /status code 409/i,
    reason: 'The data was changed by someone else.',
    hint: HINT.conflict,
  },
  fileSize: {
    test: /status code 413|payload too large/i,
    reason: 'The file is too large.',
    hint: HINT.fileSize,
  },
  validation: {
    test: /status code (400|422)/i,
    reason: 'The data was rejected by the server.',
    hint: HINT.validation,
  },
  server: {
    test: /^internal server error$|status code 5\d\d|^bad gateway$|^service unavailable$|^gateway timeout$/i,
    reason: 'The server ran into a problem.',
    hint: HINT.server,
  },
};

const STATUS_RULES = {
  400: RULES.validation,
  401: RULES.session,
  403: RULES.permission,
  404: RULES.notFound,
  409: RULES.conflict,
  413: RULES.fileSize,
  422: RULES.validation,
};

const firstValidationError = errors => {
  if (!errors || typeof errors !== 'object') return '';
  const first = Object.values(errors).flat()[0];
  return typeof first === 'string' ? first : '';
};

/** Pull a human readable text (and HTTP status, if any) out of whatever was thrown/rejected. */
const readError = error => {
  if (error === null || error === undefined) return { text: '' };
  if (typeof error === 'string') return { text: error.trim() };

  const data = error.response?.data ?? error.data;
  const status = error.response?.status ?? error.status;
  const serverText =
    (typeof data === 'string' && data.length < 300 ? data : '') ||
    data?.message ||
    firstValidationError(data?.errors) ||
    data?.title ||
    data?.error ||
    '';
  const text = serverText || error.message || '';
  return { text: typeof text === 'string' ? text.trim() : '', status };
};

/**
 * Turn any error into `{ reason, hint }`.
 *  - reason: what went wrong, in plain words
 *  - hint:   what the user can do about it (empty when nothing useful can be said)
 */
export const describeError = error => {
  const { text, status } = readError(error);

  const byText = Object.values(RULES).find(rule => rule.test.test(text));
  if (byText) return { reason: byText.reason, hint: byText.hint };

  const byStatus = status >= 500 ? RULES.server : STATUS_RULES[status];
  if (byStatus) {
    return {
      reason: text ? formatMessage(text) : byStatus.reason,
      hint: byStatus.hint,
    };
  }

  return { reason: formatMessage(text), hint: '' };
};

const show = (type, message, options = {}) => {
  // Rich content (e.g. a list of missing fields) is passed through untouched.
  const rich = isValidElement(message);
  const text = rich ? message : formatMessage(message);
  if (!text) return null;
  return toast[type](text, {
    autoClose: DURATION[type],
    // Same message while it is on screen is shown once (double clicks, retries).
    ...(rich ? {} : { toastId: `${type}:${text}` }),
    className: `hy-toast hy-toast--${type}`,
    ...options,
  });
};

const notify = {
  success: (message, options) => show('success', message, options),

  info: (message, options) => show('info', message, options),

  warning: (message, options) => show('warning', message, options),

  /** Accepts a ready-made message or any thrown/rejected value. */
  error: (messageOrError, options) => {
    if (typeof messageOrError === 'string')
      return show('error', messageOrError, options);
    const { reason, hint } = describeError(messageOrError);
    const message = [reason, hint].filter(Boolean).join(' ');
    return show(
      'error',
      message || 'Something went wrong. Please try again.',
      options
    );
  },

  /**
   * Failed API call: "Failed to <action>. <reason> <hint>"
   *   notify.apiError(error, 'save data')
   *   -> "Failed to save data. The server could not be reached. Check your internet connection and try again."
   */
  apiError: (error, action, options) => {
    const { reason, hint } = describeError(error);
    // Errors already phrased as "Failed to ..." (thrown by our own code) are not prefixed twice.
    if (/^Failed to /i.test(reason)) {
      return show('error', `${reason} ${hint || HINT.retry}`, options);
    }
    const subject = action ? `Failed to ${action}.` : 'Something went wrong.';
    const detail = [reason, hint || (reason ? '' : HINT.retry)]
      .filter(Boolean)
      .join(' ');
    return show('error', `${subject} ${detail}`, options);
  },

  /** Form validation problem: a warning, because the user can fix it right away. */
  validation: (message = MESSAGES.mandatory, options) =>
    show('warning', message, options),

  dismiss: id => toast.dismiss(id),
};

export default notify;
